import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { statsAPI, projectAPI, knowledgeAPI, newsAPI } from "../services/api";
import { motion } from "framer-motion";

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentProjects, setRecentProjects] = useState([]);
  const [recentKnowledge, setRecentKnowledge] = useState([]);
  const [recentNews, setRecentNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [statsRes, projectsRes, knowledgeRes, newsRes] =
          await Promise.all([
            statsAPI.getDashboard(),
            projectAPI.getAll({ limit: 5 }),
            knowledgeAPI.getRecent(5),
            newsAPI.getRecent(5),
          ]);

        setStats(statsRes.data.data);
        setRecentProjects(projectsRes.data.data);
        setRecentKnowledge(knowledgeRes.data.data);
        setRecentNews(newsRes.data.data);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("th-TH", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>แดชบอร์ด | งานบริการวิชาการ มหาวิทยาลัยราชภัฏศรีสะเกษ</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container-custom">
          {/* Welcome Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg p-8 mb-8"
          >
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center gap-4 mb-4 md:mb-0">
                <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center">
                  {user?.mem_img ? (
                    <img
                      src={user.mem_img}
                      alt={`${user.mem_fname} ${user.mem_sname}`}
                      className="w-20 h-20 rounded-full object-cover"
                    />
                  ) : (
                    <i className="fas fa-user-circle text-5xl text-primary-500"></i>
                  )}
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-primary-500">
                    ยินดีต้อนรับ, {user?.mem_fname} {user?.mem_sname}
                  </h1>
                  <p className="text-gray-600">
                    {user?.dpt_name} | ระดับ:{" "}
                    {user?.level === "1"
                      ? "ผู้ดูแลระบบ"
                      : user?.level === "2"
                        ? "เจ้าหน้าที่"
                        : "สมาชิก"}
                  </p>
                  <p className="text-sm text-gray-500">{user?.mem_email}</p>
                </div>
              </div>
              <button
                onClick={logout}
                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
              >
                <i className="fas fa-sign-out-alt"></i>
                ออกจากระบบ
              </button>
            </div>
          </motion.div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white"
            >
              <div className="flex justify-between items-center mb-4">
                <div className="p-3 bg-white/20 rounded-lg">
                  <i className="fas fa-project-diagram text-2xl"></i>
                </div>
                <span className="text-3xl font-bold">
                  {stats?.totalProjects || 0}
                </span>
              </div>
              <h3 className="text-lg font-semibold">โครงการทั้งหมด</h3>
              <p className="text-sm opacity-80">โครงการบริการวิชาการ</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white"
            >
              <div className="flex justify-between items-center mb-4">
                <div className="p-3 bg-white/20 rounded-lg">
                  <i className="fas fa-brain text-2xl"></i>
                </div>
                <span className="text-3xl font-bold">
                  {stats?.totalKnowledge || 0}
                </span>
              </div>
              <h3 className="text-lg font-semibold">องค์ความรู้</h3>
              <p className="text-sm opacity-80">คลังปัญญาทั้งหมด</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl shadow-lg p-6 text-white"
            >
              <div className="flex justify-between items-center mb-4">
                <div className="p-3 bg-white/20 rounded-lg">
                  <i className="fas fa-newspaper text-2xl"></i>
                </div>
                <span className="text-3xl font-bold">
                  {stats?.totalNews || 0}
                </span>
              </div>
              <h3 className="text-lg font-semibold">ข่าวประชาสัมพันธ์</h3>
              <p className="text-sm opacity-80">ข่าวสารทั้งหมด</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white"
            >
              <div className="flex justify-between items-center mb-4">
                <div className="p-3 bg-white/20 rounded-lg">
                  <i className="fas fa-eye text-2xl"></i>
                </div>
                <span className="text-3xl font-bold">
                  {stats?.totalVisitors || 0}
                </span>
              </div>
              <h3 className="text-lg font-semibold">ผู้เข้าชม</h3>
              <p className="text-sm opacity-80">เข้าชมเว็บไซต์</p>
            </motion.div>
          </div>

          {/* Recent Activities Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Projects */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <h3 className="text-lg font-semibold text-primary-500 mb-4 flex items-center">
                <i className="fas fa-project-diagram text-secondary-500 mr-2"></i>
                โครงการล่าสุด
              </h3>
              <div className="space-y-3">
                {recentProjects.length > 0 ? (
                  recentProjects.map((project) => (
                    <Link
                      key={project.project_id}
                      to={`/projects/${project.project_id}`}
                      className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <h4 className="font-medium text-gray-800 line-clamp-1">
                        {project.project_name}
                      </h4>
                      <div className="flex justify-between items-center mt-1 text-xs text-gray-500">
                        <span>
                          <i
                            className="fas fa-building mr-1"
                            style={{ color: project.dpt_color }}
                          ></i>
                          {project.dpt_name}
                        </span>
                        <span>
                          <i className="fas fa-calendar mr-1"></i>
                          {formatDate(project.created_at)}
                        </span>
                      </div>
                    </Link>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">
                    ไม่มีโครงการล่าสุด
                  </p>
                )}
                <Link
                  to="/projects"
                  className="block text-center text-primary-500 hover:text-primary-600 text-sm font-medium mt-2"
                >
                  ดูทั้งหมด <i className="fas fa-arrow-right ml-1"></i>
                </Link>
              </div>
            </motion.div>

            {/* Recent Knowledge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <h3 className="text-lg font-semibold text-primary-500 mb-4 flex items-center">
                <i className="fas fa-brain text-secondary-500 mr-2"></i>
                องค์ความรู้ล่าสุด
              </h3>
              <div className="space-y-3">
                {recentKnowledge.length > 0 ? (
                  recentKnowledge.map((knowledge) => (
                    <Link
                      key={knowledge.knowledge_id}
                      to={`/knowledge/${knowledge.knowledge_id}`}
                      className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <h4 className="font-medium text-gray-800 line-clamp-1">
                        {knowledge.title}
                      </h4>
                      <div className="flex justify-between items-center mt-1 text-xs text-gray-500">
                        <span>
                          <i className="fas fa-folder mr-1 text-primary-500"></i>
                          {knowledge.catkm_name}
                        </span>
                        <span>
                          <i className="fas fa-eye mr-1"></i>
                          {knowledge.views || 0} ครั้ง
                        </span>
                      </div>
                    </Link>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">
                    ไม่มีองค์ความรู้ล่าสุด
                  </p>
                )}
                <Link
                  to="/knowledge"
                  className="block text-center text-primary-500 hover:text-primary-600 text-sm font-medium mt-2"
                >
                  ดูทั้งหมด <i className="fas fa-arrow-right ml-1"></i>
                </Link>
              </div>
            </motion.div>

            {/* Recent News */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <h3 className="text-lg font-semibold text-primary-500 mb-4 flex items-center">
                <i className="fas fa-newspaper text-secondary-500 mr-2"></i>
                ข่าวประชาสัมพันธ์ล่าสุด
              </h3>
              <div className="space-y-3">
                {recentNews.length > 0 ? (
                  recentNews.map((news) => (
                    <Link
                      key={news.news_id}
                      to={`/news/${news.news_id}`}
                      className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <h4 className="font-medium text-gray-800 line-clamp-1">
                        {news.title}
                      </h4>
                      <div className="flex justify-end mt-1 text-xs text-gray-500">
                        <span>
                          <i className="fas fa-calendar mr-1"></i>
                          {formatDate(news.publish_date)}
                        </span>
                      </div>
                    </Link>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">
                    ไม่มีข่าวล่าสุด
                  </p>
                )}
                <Link
                  to="/news"
                  className="block text-center text-primary-500 hover:text-primary-600 text-sm font-medium mt-2"
                >
                  ดูทั้งหมด <i className="fas fa-arrow-right ml-1"></i>
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-8 bg-white rounded-xl shadow-lg p-6"
          >
            <h3 className="text-lg font-semibold text-primary-500 mb-4 flex items-center">
              <i className="fas fa-bolt text-secondary-500 mr-2"></i>
              การดำเนินการด่วน
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link
                to="/projects"
                className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-primary-50 transition-colors group"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-2 group-hover:bg-primary-200">
                  <i className="fas fa-map-marked-alt text-primary-500"></i>
                </div>
                <span className="text-sm font-medium text-gray-700">
                  ดูโครงการ
                </span>
              </Link>
              <Link
                to="/knowledge"
                className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-primary-50 transition-colors group"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-2 group-hover:bg-primary-200">
                  <i className="fas fa-brain text-primary-500"></i>
                </div>
                <span className="text-sm font-medium text-gray-700">
                  องค์ความรู้
                </span>
              </Link>
              <Link
                to="/news"
                className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-primary-50 transition-colors group"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-2 group-hover:bg-primary-200">
                  <i className="fas fa-newspaper text-primary-500"></i>
                </div>
                <span className="text-sm font-medium text-gray-700">
                  ข่าวสาร
                </span>
              </Link>
              <Link
                to="/report"
                className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-primary-50 transition-colors group"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-2 group-hover:bg-primary-200">
                  <i className="fas fa-chart-bar text-primary-500"></i>
                </div>
                <span className="text-sm font-medium text-gray-700">
                  รายงาน
                </span>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default DashboardPage;
